import { useLegacyScheduledTransfers } from '@/src/experiences/card-activity/components/feed/_highnote/ScheduledTransfers/useLegacyScheduledTransfers'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useCancelScheduledTransfer } from '@/src/common/hooks/useCancelScheduledTransfer'
import { useState } from 'react'
import { formatCurrency } from '@/src/common/utils/general'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import {
  formatOneTimePaymentDate,
  type OneTimePaymentScheduleReviewComponentWrapper,
} from '@/src/app/main/(tabs)/card/card-management/payment/oneTimePayment/[id]'

export const LegacyOneTimePaymentScheduleReview = (({
  OneTimePaymentScheduleReview,
  oneTimePaymentScheduleReviewScreenProps,
  id,
}) => {
  const Alert = useAlert(OneTimePaymentScheduleReview.name)
  const logError = useLogError()
  const [initialized, setInitialized] = useState(false)
  const {
    data: includingDataToIgnore,
    loading,
    refetch,
  } = useLegacyScheduledTransfers({
    onCompleted() {
      setInitialized(true)
    },
    onError() {
      setInitialized(true)
    },
  })

  const data = includingDataToIgnore
    ?.filter((s) => s.__typename === 'OneTimeAchTransferResponse')
    .find((s) => s.id === id)

  const [cancelScheduledTransfer, { loading: canceling, called, error }] =
    useCancelScheduledTransfer(
      // we don't want refetching to happen because the scheduled payment's details
      // data the user was reviewing must not suddenly disappear while the cancelation
      // is loading. that data shouldn't disappear until the user dismisses the modal
      { awaitRefetchQueries: false, refetchQueries: [] },
    )

  const { dismiss: dismissWithoutRefetch } = oneTimePaymentScheduleReviewScreenProps
  function dismiss() {
    void dismissWithoutRefetch()
    void refetch()
  }

  return (
    <OneTimePaymentScheduleReview
      oneTimePaymentScheduleReviewScreenProps={oneTimePaymentScheduleReviewScreenProps}
      id={id}
      initialized={initialized}
      loading={loading || canceling}
      disabled={loading || canceling || (called && !error)}
      cancel={() => {
        Alert.alert(
          'Cancellation Notice',
          'Cancellation must be received by 9:00 AM Eastern on the date your payment is scheduled.',
          [
            { text: 'Back', onPress: () => {}, style: 'cancel' },
            {
              text: CardPaymentsConstants.Strings.CancelOneTimePayment,
              onPress() {
                void cancelScheduledTransfer({
                  variables: { scheduledTransferId: id },
                  onCompleted({ cancelScheduledTransfer }) {
                    switch (cancelScheduledTransfer.__typename) {
                      case 'MesaUserError':
                        Alert.alert(
                          'There was an issue canceling your one-time payment',
                          cancelScheduledTransfer.message,
                        )
                        return
                      case 'MesaGenericOk':
                        Alert.alert(
                          CardPaymentsConstants.Strings.OneTimePaymentSuccessfullyCanceled,
                          'You will no longer be charged on the date of your scheduled payment.',
                          [
                            {
                              text: CardPaymentsConstants.Strings
                                .OneTimePaymentSuccessfullyCanceledOk,
                              onPress: dismiss,
                            },
                          ],
                          { onDismiss: dismiss },
                        )
                        return
                      default:
                        cancelScheduledTransfer satisfies never
                        logError(
                          'unhandled cancelScheduledTransfer response',
                          cancelScheduledTransfer,
                        )
                    }
                  },
                  onError(error) {
                    Alert.alert(
                      `We weren't able to cancel your one-time payment`,
                      `Please try again later`,
                    )
                    logError(error)
                  },
                })
              },

              style: 'destructive',
            },
          ],
        )
      }}
      data={
        data
          ? {
              amount: formatCurrency(data.transferAmount.text, {
                decimals: null,
                isFloat: true,
                minimumFractionDigits: 2,
                hideCurrencySymbol: false,
              }),
              status: data.status,
              date: data.incomingScheduledTransferDate
                ? formatOneTimePaymentDate(data.incomingScheduledTransferDate)
                : undefined,
              account: data.fromHighnoteExternalAccount
                ? [
                    [
                      data.fromHighnoteExternalAccount.bankName,
                      data.fromHighnoteExternalAccount.accountSubtype,
                    ]
                      .join(' ')
                      .trim() || data.fromHighnoteExternalAccount.accountName,
                    data.fromHighnoteExternalAccount.bankAccountLast4,
                  ]
                    .filter((s) => typeof s === 'string')
                    .filter((s) => s)
                    .join(' ')
                : undefined,
            }
          : undefined
      }
    />
  )
}) satisfies OneTimePaymentScheduleReviewComponentWrapper
